import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { DataTable, type Column } from '@/components/DataTable'
import { FilterPanel, SelectFilter } from '@/components/FilterPanel'
import { SearchInput } from '@/components/SearchInput'
import { StatusBadge } from '@/components/StatusBadge'
import { DocumentDetailDrawer } from '@/components/DocumentDetailDrawer'
import { useDocuments, useOrganizations } from '@/hooks/useRootQueries'
import type { DocumentItem } from '@/types/api'
import { exactTimestamp, relativeTime } from '@/utils/time'

export function Documents() {
  const [params, setParams] = useSearchParams()
  const [selectedDoc, setSelectedDoc] = useState<DocumentItem | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const orgsQuery = useOrganizations({ pageSize: 100 })
  const orgOptions = [
    { value: '', label: 'All Organizations' },
    ...(orgsQuery.data?.data?.map((o) => ({ value: o.id, label: o.name })) || []),
  ]

  const filters = useMemo(
    () => ({
      search: params.get('search') ?? '',
      organization: params.get('organization') ?? '',
      status: params.get('status') ?? '',
      category: params.get('category') ?? '',
      page: Number(params.get('page') ?? '1'),
      pageSize: 25,
    }),
    [params],
  )

  function updateParam(key: string, value: string) {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    if (key !== 'page') next.set('page', '1')
    setParams(next)
  }

  function handleRowClick(row: DocumentItem) {
    setSelectedDoc(row)
    setDrawerOpen(true)
  }

  const documents = useDocuments(filters)

  const columns: Column<DocumentItem>[] = [
    {
      key: 'title',
      header: 'Title / Category',
      render: (row) => (
        <div>
          <div className="font-semibold text-slate-900">{row.title}</div>
          <div className="font-mono text-[10.5px] text-slate-500">{row.category || 'General'}</div>
        </div>
      ),
    },
    {
      key: 'organization',
      header: 'Organization',
      render: (row) => (
        <div>
          <div className="font-bold text-slate-800 text-xs truncate max-w-[150px]">{row.orgName || 'Organization'}</div>
          {row.orgId && (
            <div className="font-mono text-[10px] text-slate-400 truncate max-w-[120px]">ID: {row.orgId.slice(-6)}</div>
          )}
        </div>
      ),
    },
    {
      key: 'signers',
      header: 'Signers Progress',
      render: (row) => (
        <div className="flex flex-wrap gap-1 max-w-[220px]">
          {Array.isArray(row.signers) && row.signers.length > 0 ? (
            row.signers.map((s) => (
              <span
                key={s.id}
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                  s.status.toLowerCase() === 'signed'
                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                    : s.status.toLowerCase() === 'declined'
                    ? 'bg-rose-50 text-rose-700 border border-rose-200'
                    : 'bg-amber-50 text-amber-700 border border-amber-200'
                }`}
                title={`${s.name} (${s.email}) - ${s.status}`}
              >
                {s.name || s.email}: {s.status}
              </span>
            ))
          ) : (
            <span className="text-slate-400 text-[11px]">—</span>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'expiry',
      header: 'Expiry Date',
      render: (row) => {
        if (!row.expiryDate) return <span className="text-slate-400 text-[11px]">No expiry</span>
        const isPast = new Date(row.expiryDate).getTime() < Date.now()
        return (
          <div>
            <div className={`font-mono text-xs ${isPast ? 'text-amber-700 font-bold' : 'text-slate-700'}`}>
              {exactTimestamp(row.expiryDate)}
            </div>
            {isPast && <span className="text-[10px] font-mono text-amber-600 font-bold">Lapsed</span>}
          </div>
        )
      },
    },
    {
      key: 'createdAt',
      header: 'Created',
      render: (row) => (
        <div className="font-mono text-xs text-slate-600">{relativeTime(row.createdAt)}</div>
      ),
    },
  ]

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-slate-900 tracking-tight">Platform Documents</h1>
          <p className="mt-0.5 text-xs font-medium text-slate-500">
            Platform-wide governance, execution tracking, and cryptographic signature verification.
          </p>
        </div>
      </div>

      {/* Quick Filter Presets */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mr-1">Status:</span>
        {['', 'expired', 'completed', 'sent', 'voided'].map((st) => (
          <button
            key={st}
            onClick={() => updateParam('status', st)}
            className={`rounded-lg px-3 py-1 text-xs font-semibold capitalize transition-all ${
              filters.status === st
                ? 'bg-sky-600 text-white shadow-xs'
                : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            {st ? (st === 'expired' ? '⚠️ Expired' : st) : 'All Statuses'}
          </button>
        ))}
      </div>

      <FilterPanel>
        <div className="min-w-[220px] flex-1">
          <span className="mb-1 block text-[10.5px] font-bold text-slate-500 uppercase">Search Documents</span>
          <SearchInput
            value={filters.search}
            onChange={(v) => updateParam('search', v)}
            placeholder="Document title, category or ID…"
          />
        </div>
        <SelectFilter
          label="Organization"
          value={filters.organization}
          options={orgOptions}
          onChange={(v) => updateParam('organization', v)}
        />
        <SelectFilter
          label="Status"
          value={filters.status}
          options={[
            { value: 'expired', label: '⚠️ Expired' },
            { value: 'completed', label: 'Completed' },
            { value: 'sent', label: 'Sent' },
            { value: 'draft', label: 'Draft' },
            { value: 'voided', label: 'Voided' },
            { value: 'declined', label: 'Declined' },
          ]}
          onChange={(v) => updateParam('status', v)}
        />
        <SelectFilter
          label="Category"
          value={filters.category}
          options={[
            { value: 'Contracts', label: 'Contracts' },
            { value: 'NDAs', label: 'NDAs' },
            { value: 'Invoices', label: 'Invoices' },
            { value: 'HR', label: 'HR' },
            { value: 'Agreements', label: 'Agreements' },
          ]}
          onChange={(v) => updateParam('category', v)}
        />
        {(filters.search || filters.organization || filters.status || filters.category) && (
          <button
            onClick={() => setParams(new URLSearchParams())}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 hover:text-slate-900 shadow-xs"
          >
            Reset
          </button>
        )}
      </FilterPanel>

      <DataTable
        columns={columns}
        rows={documents.data?.data ?? []}
        getRowId={(row) => row.id}
        isLoading={documents.isLoading}
        isError={documents.isError}
        errorMessage="Could not load documents."
        onRetry={() => documents.refetch()}
        emptyTitle="No documents found"
        emptyDescription="Try adjusting your search or filters."
        onRowClick={(row) => handleRowClick(row)}
        page={filters.page}
        totalPages={documents.data?.meta.totalPages ?? 1}
        totalItems={documents.data?.meta.totalItems ?? 0}
        onPageChange={(p) => updateParam('page', String(p))}
      />

      {/* Rich Document Detail Drawer */}
      <DocumentDetailDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false)
          setSelectedDoc(null)
        }}
        initialDocument={selectedDoc}
        documentId={selectedDoc?.id}
      />
    </div>
  )
}
